// Single sudoku input handler: translates raw pointer + keyboard events
// from the canvas / window into high-level callbacks the scene subscribes
// to. The scene owns mode state (pencil on/off, selected cell, …); this
// handler is stateless except for the listener bookkeeping needed by
// `dispose()`.

import { canvasCoordsFromEvent } from '@bn-games/input-core';
import type { NonEmptyDigit } from '../game/types.js';
import {
  cellAtPoint,
  numpadButtonAtPoint,
  pauseButtonAtPoint,
} from '../layout/LayoutManager.js';
import type { Layout, NumpadButtonId } from '../layout/types.js';
import { classifyKey, type ArrowDirection, type KeyAction } from './KeyMap.js';

export interface InputCallbacks {
  // Pointer
  onSelectCell?: (cellIndex: number | null) => void;
  onNumpadDigit?: (digit: NonEmptyDigit) => void;
  onNumpadPencilToggle?: () => void;
  onNumpadErase?: () => void;
  onNumpadUndo?: () => void;
  onNumpadHint?: () => void;
  /** Tap on the status-bar pause button. Primary mobile entry to PauseMenu. */
  onPauseTap?: () => void;
  // Keyboard
  onKeyDigit?: (digit: NonEmptyDigit) => void;
  onKeyArrow?: (direction: ArrowDirection) => void;
  onKeyErase?: () => void;
  onKeyUndo?: () => void;
  onKeyRedo?: () => void;
  onKeyPencilToggle?: () => void;
  onKeyEscape?: () => void;
}

export interface InputHandlerOptions {
  readonly canvas: HTMLCanvasElement;
  /** Layout snapshot lookup — re-read on every event so the handler picks
   *  up viewport resizes automatically without a re-subscribe step. */
  readonly getLayout: () => Layout;
  readonly callbacks: InputCallbacks;
}

const DIGIT_BUTTON_PREFIX = 'digit-';

function digitFromButtonId(id: NumpadButtonId): NonEmptyDigit | null {
  if (!id.startsWith(DIGIT_BUTTON_PREFIX)) return null;
  const n = Number(id.slice(DIGIT_BUTTON_PREFIX.length));
  if (n >= 1 && n <= 9) return n as NonEmptyDigit;
  return null;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  if (target instanceof HTMLSelectElement) return true;
  return target.isContentEditable;
}

export class InputHandler {
  private readonly disposers: Array<() => void> = [];

  constructor(private readonly opts: InputHandlerOptions) {
    this.attachPointer();
    this.attachKeyboard();
  }

  private attachPointer(): void {
    const { canvas } = this.opts;

    // Tap is recognized on `pointerup` to avoid the mousedown+touchstart
    // double-fire problem. Pointer Events normalize touch and mouse into a
    // single stream, so one listener handles both.
    const onUp = (e: PointerEvent): void => {
      if (!e.isPrimary) return;
      this.handleTap(e);
    };
    const onDragStart = (e: DragEvent): void => e.preventDefault();

    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('dragstart', onDragStart);

    this.disposers.push(
      () => canvas.removeEventListener('pointerup', onUp),
      () => canvas.removeEventListener('dragstart', onDragStart),
    );
  }

  private handleTap(e: PointerEvent): void {
    const layout = this.opts.getLayout();
    const { x, y } = canvasCoordsFromEvent(e, this.opts.canvas, {
      width: layout.viewport.width,
      height: layout.viewport.height,
    });

    // Pause button wins first — it sits in the status bar, which is outside
    // both the board and the numpad regions, so order between the other two
    // checks is independent.
    if (pauseButtonAtPoint(layout, x, y)) {
      this.opts.callbacks.onPauseTap?.();
      return;
    }

    const button = numpadButtonAtPoint(layout, x, y);
    if (button !== null) {
      this.dispatchNumpadButton(button.id);
      return;
    }

    const cell = cellAtPoint(layout, x, y);
    if (cell !== null) {
      this.opts.callbacks.onSelectCell?.(cell.index);
      return;
    }

    // Tap outside the interactive regions → deselect.
    this.opts.callbacks.onSelectCell?.(null);
  }

  private dispatchNumpadButton(id: NumpadButtonId): void {
    const { callbacks } = this.opts;
    if (id === 'pencil') {
      callbacks.onNumpadPencilToggle?.();
      return;
    }
    if (id === 'erase') {
      callbacks.onNumpadErase?.();
      return;
    }
    if (id === 'undo') {
      callbacks.onNumpadUndo?.();
      return;
    }
    if (id === 'hint') {
      callbacks.onNumpadHint?.();
      return;
    }
    const digit = digitFromButtonId(id);
    if (digit !== null) callbacks.onNumpadDigit?.(digit);
  }

  private attachKeyboard(): void {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (isEditableTarget(e.target)) return;
      const action = classifyKey(e);
      if (action === null) return;
      // Pencil-toggle must not auto-repeat on key-hold; everything else is
      // safe to repeat (digits/erase/undo/redo/arrows benefit from repeat).
      if (action.kind === 'pencil-toggle' && e.repeat) return;
      e.preventDefault();
      this.dispatchKeyAction(action);
    };
    window.addEventListener('keydown', onKeyDown);
    this.disposers.push(() => window.removeEventListener('keydown', onKeyDown));
  }

  private dispatchKeyAction(action: KeyAction): void {
    const { callbacks } = this.opts;
    switch (action.kind) {
      case 'digit':
        callbacks.onKeyDigit?.(action.digit);
        return;
      case 'arrow':
        callbacks.onKeyArrow?.(action.direction);
        return;
      case 'erase':
        callbacks.onKeyErase?.();
        return;
      case 'undo':
        callbacks.onKeyUndo?.();
        return;
      case 'redo':
        callbacks.onKeyRedo?.();
        return;
      case 'pencil-toggle':
        callbacks.onKeyPencilToggle?.();
        return;
      case 'escape':
        callbacks.onKeyEscape?.();
        return;
    }
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers.length = 0;
  }
}

export type { ArrowDirection } from './KeyMap.js';
